import { useMemo } from "react";
import { CANVAS_TITLE } from "@tellescope/constants";
import {
  canvasAllergyOptions,
  useCanvasAllergiesQuery,
  useTellescopeInfiniteQuery,
  type TellescopeModelFromName,
} from "@tellescope/tanstack-query";
import { handleCustomFilter } from "../../utils";

export interface AllergyOption {
  id: string;
  code: string;
  display: string;
  system: string;
}

export interface UseAllergyCatalogArgs {
  dataSource?: string;
  debouncedSearchQuery: string;
  allergies?: TellescopeModelFromName<"allergy_codes">[];
  customFilter?: (
    searchTerm: string,
    allergies: TellescopeModelFromName<"allergy_codes">[]
  ) => TellescopeModelFromName<"allergy_codes">[];
}

const dedupeByDisplay = (options: AllergyOption[]) =>
  options.reduce<AllergyOption[]>(
    (deduped, option) => (deduped.some((seen) => seen.display === option.display) ? deduped : [...deduped, option]),
    []
  );

export const useAllergyCatalog = ({
  dataSource,
  debouncedSearchQuery,
  allergies,
  customFilter,
}: UseAllergyCatalogArgs) => {
  const isCanvas = dataSource === CANVAS_TITLE && allergies === undefined && !customFilter;

  const canvasQuery = useCanvasAllergiesQuery({ query: isCanvas ? debouncedSearchQuery : undefined });

  const allergyCodesQuery = useTellescopeInfiniteQuery({
    model: "allergy_codes",
    method: "getSome",
    request: {
      search: debouncedSearchQuery && !customFilter ? { query: debouncedSearchQuery } : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: allergies === undefined && !isCanvas,
  });

  const options = useMemo<AllergyOption[]>(() => {
    if (isCanvas) {
      return canvasAllergyOptions(canvasQuery.data?.data).map((option) => ({ ...option, id: option.code }));
    }
    return handleCustomFilter({
      filterOptions: allergies,
      customFilter,
      debouncedSearchQuery,
      fallbackOptions: allergyCodesQuery.data ?? [],
      getStrings: (option) => [option.code, option.display],
    });
  }, [allergies, allergyCodesQuery.data, canvasQuery.data?.data, customFilter, debouncedSearchQuery, isCanvas]);

  return {
    options: useMemo(() => dedupeByDisplay(options), [options]),
    isLoading: isCanvas ? canvasQuery.isLoading : allergyCodesQuery.isLoading,
  };
};
